import { useTranslation } from "react-i18next";
import {useFetchWrapper} from "./useFetchWrapper";
import {useConfig} from "./useConfig";
import {useLocalStorageNew} from "./useLocalStorageNew";
import {INTEGRATION_TYPES} from "../utils/constants";
import {notifyError} from "../utils/helpers";

export const useCanvasOAuth = () => {
    const { t } = useTranslation();
    const fetchWrapper = useFetchWrapper();
    const config = useConfig();
    const [, getInstitution] = useLocalStorageNew(window.location.hostname, {});

    // Creates an OAuthToken record and returns the Canvas authorization URL, then navigates the current
    // tab there - used when a Canvas action is blocked by a missing/invalid token. Canvas's own
    // oauth/callback redirect brings the browser straight back to this page afterward. userId/
    // institutionId are resolved server-side from the Authorization bearer token fetchWrapper already
    // attaches (see processOauthGrant) - not passed here.
    function redirectToCanvasAuthorization() {
        const params = new URLSearchParams({
            callbackUrl: window.location.href,
            integrationType: INTEGRATION_TYPES.CANVAS,
        });
        return fetchWrapper.get({
            url: `${config.api.integration}/lti/oauth/grant?${params.toString()}`,
        }).then(result => {
            window.location.href = result.result.authURL;
        }).catch(err => {
            notifyError(t("auth.canvasLoginUnreachable"));
            console.log("Error getting Canvas OAuth authorization URL.", err);
        });
    }

    // Institutions configured before the OAuth 2.0 migration have no Canvas Developer Key
    // (apiAccessKeyClientId/apiAccessKeyClientSecret) and still call Canvas with the legacy
    // apiAccessToken, so no per-user authorization is needed. Returns false when the Canvas config
    // isn't available locally, leaving the decision to the backend validate call.
    function isLegacyCanvasConfig() {
        const {ssoConfig={}} = getInstitution();
        if (!ssoConfig || !ssoConfig[INTEGRATION_TYPES.CANVAS]) {
            return false;
        }

        try {
            const canvasConfig = JSON.parse(ssoConfig[INTEGRATION_TYPES.CANVAS]);
            return !canvasConfig.apiAccessKeyClientId || !canvasConfig.apiAccessKeyClientSecret;
        } catch (e) {
            return false;
        }
    }

    // Checks whether the current user has a valid Canvas OAuth token before a Canvas action is
    // triggered (non-Canvas types and legacy-token Canvas institutions are always authorized). On
    // false, redirects the current tab to Canvas's authorization page instead of letting the action
    // call fail.
    function ensureLMSAuthorized(intType) {
        if (intType !== INTEGRATION_TYPES.CANVAS || isLegacyCanvasConfig()) {
            return Promise.resolve(true);
        }

        return fetchWrapper.get({
            url: `${config.api.integration}/lti/oauth/validate?integrationType=${intType}`,
        }).then(result => {
            if (result.success && result.result && result.result.valid) {
                return true;
            }
            return redirectToCanvasAuthorization().then(() => false);
        }).catch(err => {
            console.log("Error validating LMS OAuth token.", err);
            return redirectToCanvasAuthorization().then(() => false);
        });
    }

    return {
        ensureLMSAuthorized,
    };
}
